import { SimConstants } from "@highmarch/domain";
import { audio } from "../audio/audio";
import { WYRM_CUE, WyrmHurtGate, WyrmRoarCue, nextGlitter, wingCrossed, wyrmArrivalCues } from "../audio/wyrmCues";
import { WYRM_ARRIVAL } from "../ui/wyrmModel";

/**
 * The Starfall Wyrm's sound in the level (M40; audio/wyrmCues.ts holds the rules, art/audio/v6/wyrm_sfx.py the set):
 * level/wyrmMoment.ts tells it the moments — the arrival or the paid return, a roar, its health, the escape, the choice
 * and the reveal — and runs `frame` while it flies, for its wingbeats (on the flight loop's downstroke) and its glitter
 * (paced in game time, so a paused battle stays silent). The kill's cry and the crash are played by the picture
 * (render/effects.ts on the killing round's landing, render/views/wyrmView.ts on the crash frame).
 */
export class WyrmAudio {
  private readonly hurt = new WyrmHurtGate();
  private wingClip = "";
  private wingPhase = -1;
  private glitterIn = 0;
  private lastTick = -1;

  /** It appears: a fresh arrival or a paid retry (`quiet`: a resumed battle with it already there — no entrance). */
  arrive(retry: boolean, quiet: boolean): void {
    this.hurt.reset();
    this.glitterIn = nextGlitter(Math.random()) + WyrmAudio.GlitterAfterArrival;
    if (quiet) return;
    for (const c of wyrmArrivalCues(retry, WYRM_ARRIVAL.RoarMs / 1000)) audio.play(c.cue, c.delay);
  }

  /** A roar as it is hurt (the `roar` clip just started). */
  roar(): void { audio.play(WyrmRoarCue.cue, WyrmRoarCue.delay); }
  /** Its health this tick (0..1): a glassy crack each time it has lost another step. */
  health(frac: number): void { if (this.hurt.feed(frac)) audio.play(WYRM_CUE.hurt); }
  flee(): void { audio.play(WYRM_CUE.flee); }
  choice(): void { audio.play(WYRM_CUE.choice); }
  unlock(): void { audio.play(WYRM_CUE.unlock); }

  /**
   * Every frame while it may be flying: `wing` is its flight loop now (EnemyViews.wyrmWing), null when none; `tick` the
   * sim's tick (the glitter counts the game's time, not the frame's).
   */
  frame(wing: { readonly clip: string; readonly phase: number } | null, tick: number): void {
    if (wing === null) { this.wingPhase = -1; this.lastTick = -1; return; }
    if (wing.clip === this.wingClip && wingCrossed(wing.clip, this.wingPhase, wing.phase)) audio.play(WYRM_CUE.wing);
    this.wingClip = wing.clip; this.wingPhase = wing.phase;
    if (this.lastTick >= 0 && tick > this.lastTick) {
      this.glitterIn -= (tick - this.lastTick) / SimConstants.TicksPerSecond;
      if (this.glitterIn <= 0) { audio.play(WYRM_CUE.glitter); this.glitterIn = nextGlitter(Math.random()); }
    }
    this.lastTick = tick;
  }

  /** After an arrival, the first glitter waits for the stingers to finish (s). */
  static readonly GlitterAfterArrival = 2.5;
}
